import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import {ToastContainer} from 'react-toastify';
import config from '../../../config/env/all';
import Modals from '../Modal/Modal';
import socketService from '../../services/socket';
import {
  ActionModalShowVideosessionInvite, ActionModalHide, ActionModalShowVideoPollInvite,
} from '../../store/actions/actions-modal';
import {ActionGetUserInfo, ActionSignOut} from '../../store/actions/actions-user';
import {history} from '../../store/store';
import {ActionCallInvitedDeclined} from '../../store/actions/actions-videosession';
import CookiesNotification from '../CookiesNotification';
import HeaderInfo from '../Header/HeaderInfo/HeaderInfo';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import URL_MAP from '../../constants/urls';
import SlowConnection from '../SlowConnection';
import {ROUTES} from '../../configs/routes';
import {routesMeta as HTML_LAYOUTS_ROUTES} from '../../pages/HtmlLayouts/routes';
import {ActionGetNewVideoPollsCounter} from '../../store/actions/actions-pages';
import {LanguageContext} from '../../context/LanguageContext';
import {COOKIES_USER_STORE_KEY} from '../../constants/cookies';

import '../../assets/less/main.less';
import {NotificationError} from '../../services/notifications';

const notIncludeSidebarAndHeader = [URL_MAP.BASE_URL, URL_MAP.PASSED_VIDEO_POLL, [URL_MAP.VIDEO_POLLS, URL_MAP.START],
  URL_MAP.ONLINE_POLL,
];

function RootLayout({
  loggedIn, language, children, tokenData, router, user,
}) {
  const [lng, setLng] = useState(null);

  const dispatch = useDispatch();

  let backendS = null;

  const onStorageChange = useCallback((e) => {
    if (e.key === COOKIES_USER_STORE_KEY && e.oldValue && !e.newValue) {
      dispatch(ActionSignOut(true));
    }
  }, []);

  const addListenerLocalStorageUpdate = useCallback(() => {
    window.addEventListener('storage', onStorageChange);
  }, []);

  const removeListenerLocalaStorageUpdate = useCallback(() => {
    window.removeEventListener('storage', onStorageChange);
  }, []);

  const onBrowserHistoryChanges = useCallback(() => {
    history.listen(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  onBrowserHistoryChanges();

  const removeListeners = useCallback((socket) => {
    if (socket) {
      socket.off('call-invited');
      socket.off('call-invited-cancelled');
      socket.off('call-invited-declined');
      socket.off('call-invited-accepted');
      socket.off('invite-video-poll');
    }
  }, [backendS]);

  const initSocket = useCallback(() => {
    const extraHeaders = loggedIn ? {Authorization: `Bearer ${tokenData.access_token}`} : {};
    if (backendS) {
      removeListeners(backendS);
    }
    backendS = socketService.createSocket('backendS', config.ws.backend.url, {
      protocol: config.ws.backend.protocol,
      extraHeaders,
    });
    backendS.on('call-invited', (data) => {
      dispatch(ActionModalShowVideosessionInvite(data));
    });
    backendS.on('call-invited-cancelled', () => {
      dispatch(ActionModalHide());
    });
    backendS.on('call-invited-declined', (data) => {
      dispatch(ActionCallInvitedDeclined(data));
    });
    backendS.on('call-invited-accepted', () => {});
    backendS.on('invite-video-poll', (data) => {
      dispatch(ActionGetNewVideoPollsCounter());
      dispatch(ActionModalShowVideoPollInvite(data));
    });
  }, [loggedIn, tokenData, config]);

  const getUserData = useCallback(() => {
    if (user.role && !loggedIn) {
      NotificationError('Время сессии истекло');
      return dispatch(ActionSignOut());
    }
    if (loggedIn) {
      dispatch(ActionGetUserInfo());
    }
  }, [loggedIn, user]);

  const hideLayouts = useMemo(() => {
    const {pathname} = router.location;
    const urlArr = pathname.split('/');
    const currentPath = urlArr[1];

    return notIncludeSidebarAndHeader.some((route) => {
      if (Array.isArray(route)) {
        return route.every((_r) => urlArr.includes(_r));
      }
      return currentPath === route;
    });
  }, [router, notIncludeSidebarAndHeader]);

  useEffect(() => {
    getUserData();
    addListenerLocalStorageUpdate();

    return () => {
      if (backendS) {
        removeListeners(backendS);
        removeListenerLocalaStorageUpdate();
      }
    };
  }, []);

  useEffect(() => {
    initSocket();
  }, [tokenData]);

  useEffect(() => {
    if (!lng && language) {
      setLng(language);
    } else {
      window.location.reload();
    }
  }, [language]);

  const [, mainPagePath, nestedPagePath, ...otherPaths] = router.location.pathname.split('/');

  if (`/${mainPagePath}` === ROUTES.HTML_LAYOUTS.route) {
    const routeLayout = HTML_LAYOUTS_ROUTES.find((rLayout) => rLayout.path === nestedPagePath);
    if (routeLayout && routeLayout.emptyPage) {
      return children;
    }
  }

  const skipCookieNotificationDelay = mainPagePath === URL_MAP.VIDEO_POLLS
    && otherPaths && otherPaths[0] && otherPaths[0] === URL_MAP.START;
  return (
    <LanguageContext.Provider value={language}>
      <div id="outer-container">
        <div id="app" className="app">
          {!hideLayouts && (
            <>
              <Sidebar />
              <Header />
              <HeaderInfo />
            </>
          )}
          {children}
        </div>
        <ToastContainer />
        <Modals />
        <CookiesNotification skipDelay={skipCookieNotificationDelay} />
        <SlowConnection />
      </div>
    </LanguageContext.Provider>
  );
}

RootLayout.defaultProps = {
  user: {},
  tokenData: {},
  router: {},
  language: 'ru',
  loggedIn: false,
};

RootLayout.propTypes = {
  user: PropTypes.shape({}),
  router: PropTypes.shape({}),
  tokenData: PropTypes.shape({}),
  language: PropTypes.string,
  loggedIn: PropTypes.bool,
};

function mapStateToProps({
  router, rUser, rSession, rPages
}) {
  return {
    router,
    user: rUser,
    language: rPages.language,
    tokenData: rSession.tokenData,
    loggedIn: rSession.LOGGED_IN,
  };
}

export default connect(mapStateToProps, null)(withRouter(RootLayout));
